import React, { useContext } from 'react';
import styled from 'styled-components';
import Img from 'gatsby-image';
import Slider from 'react-slick';
import Vimeo from './Vimeo';
import LocaleLink from './LocaleLink';
import LocaleBlock from './LocaleBlock';
import LocaleContext from './LocaleContext';
import { tr } from '../utils/translations';
import { breakpoints } from '../styles/Variables';
import arrowLeft from '../assets/images/arrow-left-peach.svg';
import arrowLeftHover from '../assets/images/arrow-left-peach-hover.svg';
import arrowRight from '../assets/images/arrow-right-peach.svg';
import arrowRightHover from '../assets/images/arrow-right-peach-hover.svg';

const PropertyPreviewStyles = styled.div`
  h3 {
    border-bottom: 1px solid var(--peach);
    text-transform: none;
    text-align: left;
    margin-bottom: 0;
  }

  .info {
    padding: 2rem var(--col1) 4rem var(--col1);
    text-align: center;

    p {
      text-align: left;
    }
  }

  .button {
    margin-top: 1rem;
    color: var(--green);
    background-color: var(--peach);

    &:hover {
      background: transparent;
      color: var(--peach);
    }
  }

  .subtitle {
    font-size: var(--fsBody);
    display: block;
    font-style: italic;
    padding-bottom: 0.25rem;
  }

  @media (min-width: ${breakpoints.breakLg}) {
    .property {
      padding: 0 var(--col2);
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 2rem;
      margin-bottom: 4rem;
      align-items: center;

      .info {
        text-align: left;
        padding: 0;
        vertical-align: middle;
      }
    }
  }
`;

// When set to carousel
export const GalleryStyles = styled.div`
  position: relative;
  width: 100%;

  /* Slick styles */
  .slick-slide {
    z-index: 1;
  }

  .slick-slide[aria-hidden='true'] {
    z-index: 0;
  }

  .slick-arrow {
    position: absolute;
    top: 62vw;
    z-index: 1;
    background-color: transparent;
    background-repeat: no-repeat;
    background-size: contain;
    border: none;
    text-indent: -9999px;
    width: 2rem;
    height: 2rem;
    cursor: pointer;
    transition: all 0.5s;
    outline: none;

    &:hover {
      transition: all 0.5s;
    }

    @media (min-width: ${breakpoints.breakLg}) {
      top: 30%;
    }
  }

  .slick-prev {
    left: 1rem;
    background-image: url(${arrowLeft});

    &:hover {
      background-image: url(${arrowLeftHover});
    }

    @media (min-width: ${breakpoints.breakLg}) {
      left: 2rem;
    }
  }

  .slick-next {
    right: 1rem;
    background-image: url(${arrowRight});

    &:hover {
      background-image: url(${arrowRightHover});
    }

    @media (min-width: ${breakpoints.breakLg}) {
      right: 2rem;
    }
  }

  .slick-dots {
    list-style: none;
    padding: 0;
    margin: 0;
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 1.25rem;
    text-align: center;
    height: 0.75rem;

    li {
      display: inline-block;
      text-indent: -9999px;
      border-radius: 50%;
      border: 1px solid var(--peach);
      width: 0.75rem;
      height: 0.75rem;
      margin: 0 0.25rem;

      &.slick-active {
        background-color: var(--peach);
      }
    }

    @media (min-width: ${breakpoints.breakLg}) {
      bottom: unset;
      top: -4rem;
    }
  }
`;

const carouselSettings = {
  dots: true,
  infinite: true,
  speed: 1000,
  slidesToShow: 1,
  slidesToScroll: 1,
  fade: true,
  autoplay: false,
  arrows: true,
};

const PropertyTypesPreview = ({ propertyTypes, styles, carousel }) => {
  const [locale] = useContext(LocaleContext);
  // Allow overriding styles
  const Styles = styles || PropertyPreviewStyles;

  // Loop over properties here so that we can choose how to wrap it
  const properties = propertyTypes.map((propertyType, index) => {
    const { vimeoID } = propertyType;
    return (
      // Wrapping in extra div since slider will override styles
      // of whatever is wrapping each bit of content
      <div key={propertyType.slug}>
        <div
          className="property"
          key={`propertyType_${index}`}
          data-sal="fade"
          data-sal-duration="1500"
        >
          <LocaleLink
            to={`/${propertyType.slug}`}
            key={`propertyType${propertyType.slug}`}
          >
            {vimeoID && <Vimeo vimeoID={vimeoID} heightRatio={66} />}
            {!vimeoID && (
              <Img
                fluid={propertyType.image.asset.fluid}
                alt={propertyType.name}
              />
            )}
          </LocaleLink>
          <div className="info">
            <h3>
              {propertyType.name}
              {propertyType.subtitle && (
                <span className="subtitle">{propertyType.subtitle}</span>
              )}
            </h3>
            <LocaleBlock content={propertyType.introduction} />
            {propertyType.bookURL && (
              <a className="button cta" href={propertyType.bookURL}>
                {tr('cta', 'bookNow', locale)}
              </a>
            )}
            {!propertyType.bookURL && (
              <LocaleLink className="button cta" to={`/${propertyType.slug}`}>
                {tr('cta', 'viewMore', locale)}
              </LocaleLink>
            )}
          </div>
        </div>
      </div>
    );
  });

  return (
    <Styles>
      {carousel && (
        <>
          <GalleryStyles>
            <Slider {...carouselSettings}>{properties}</Slider>
          </GalleryStyles>
        </>
      )}
      {!carousel && properties}
    </Styles>
  );
};

export default PropertyTypesPreview;
